/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getContactsAction, resetStateAction, getListContactFileAction } from './actions';
import {
	getContactsSelector,
	getIsContactsLoadingSelector,
	getPaginationSelector,
	getIsDownloadLoadingSelector,
} from './selectors';
import { TABLE_COLUMNS } from './constants';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import { ModalHeader } from '../addresses/components/modal-header';
import { resetStateAction as resetAddressModalStateAction } from '../address-modal/actions';
import { resetStateAction as resetAddressStateAction } from '../addresses/actions';
import { getIsAvailableContactsButtonsSelector } from '../profile/selectors';
import { getIsAuditTabShownSelector } from '../address-modal/selectors';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';
import closeModal from '../../utils/closeModal';
import cancelModal from '../../utils/cancelModal';

const ContactsContainerWrapper = ({
	onGetContacts,
	contacts,
	onResetState,
	onResetAddressModalState,
	onResetAddressState,
	isAvailable,
	isContactsLoading,
	isAuditTabShown,
	onGetContactFile,
	isDownloadLoading,
}) => {
	useInjectSaga({ key: 'contactsSaga', saga });
	useInjectReducer({ key: 'contactsReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	const modal = query.get('modal');

	const id = query.has('addressId') ? query.get('addressId') : query.get('id');

	const mode = query.get('mode');

	useEffect(() => {
		if (!contacts.length) {
			onGetContacts({ searchQuery: query.toString(), id });
		}
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'contact');
		query.set('mode', 'add');
		onResetState();

		history.push(`${pathname}?${query}`);
	};

	const handleNavItemClick = (modal, tab) => {
		if (modal === 'address') {
			onResetState();
		}

		query.set('modal', modal);

		if (tab) {
			query.set('tab', tab);
		}

		history.push(`${pathname}?${query}`);
	};

	const handleModalCancel = () => {
		query.delete('contactsSearch');
		query.delete('childrenSorts');
		query.delete('childrenFilters');
		query.delete('addressId');
		query.delete('mode');

		if (pathname.includes('partners')) {
			query.set('modal', 'addresses');
			query.delete('tab');
			onResetAddressState();
		} else {
			cancelModal(query);
		}

		onResetState();
		onResetAddressModalState();

		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		onResetState();
		onResetAddressModalState();

		closeModal(query);
		history.push(`${pathname}?${query}`);
	};

	const handleDownloadContactsButtonClick = () => {
		onGetContactFile({ exportUrl: `/api/addresses/${id}/contacts/export` });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('childrenSorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetContacts({ searchQuery: query.toString(), id });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.set('childrenFilters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetContacts({ searchQuery: query.toString(), id });
		}
	});

	return (
		<ModalWindow
			onClose={handleModalClose}
			isModalShown
			navItems={[
				{
					id: 'info',
					text: 'Информация',
					onClick: () => handleNavItemClick('address'),
					isActive: modal === 'address',
				},
				{
					id: 'contacts',
					text: 'Контактные лица',
					onClick: () => handleNavItemClick('contacts'),
					isActive: modal === 'contacts',
				},
				{
					id: 'audit',
					text: 'Изменения',
					onClick: () => handleNavItemClick('address', 'audit'),
					isActive: modal === 'address' && !mode && isAuditTabShown,
				},
			]}
			modalContent={
				<DataGridComponent
					headerText="Контактные лица"
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					columns={TABLE_COLUMNS()}
					rows={contacts}
					isTableDataLoading={isContactsLoading}
					pagination={null}
					isChildren
					isWithoutFooter
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
					filterKey="childrenFilters"
					sortKey="childrenSorts"
				/>
			}
			modalHeader={
				<ModalHeader
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					onDownloadButtonClick={isAvailable.export ? handleDownloadContactsButtonClick : null}
					onModalClose={handleModalClose}
					isDownloadLoading={isDownloadLoading}
				/>
			}
			footerButtons={
				<ModalFooterButtons
					rightButtons={[
						{
							onClick: handleModalCancel,
							text: 'Закрыть',
							color: 'secondary',
						},
					]}
				/>
			}
			style={{ width: '900px' }}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	contacts: getContactsSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableContactsButtonsSelector(),
	isContactsLoading: getIsContactsLoadingSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isDownloadLoading: getIsDownloadLoadingSelector(),
});

const mapDispatchToProps = {
	onGetContactFile: getListContactFileAction,
	onGetContacts: getContactsAction,
	onResetState: resetStateAction,
	onResetAddressModalState: resetAddressModalStateAction,
	onResetAddressState: resetAddressStateAction,
};

ContactsContainerWrapper.propTypes = {
	onGetContactFile: PropTypes.func.isRequired,
	onGetContacts: PropTypes.func.isRequired,
	contacts: PropTypes.array.isRequired,
	onResetState: PropTypes.func.isRequired,
	onResetAddressModalState: PropTypes.func.isRequired,
	onResetAddressState: PropTypes.func.isRequired,
	isContactsLoading: PropTypes.bool.isRequired,
	isDownloadLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isAuditTabShown: PropTypes.bool.isRequired,
};

const ContactsContainer = connect(mapStateToProps, mapDispatchToProps)(ContactsContainerWrapper);

export { ContactsContainer };
